import { useFormik } from "formik";
import { useSelector } from "react-redux";

import React, { useState } from "react";
import * as Yup from "yup";
import { Modal, Form } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-hot-toast";
import CustomButton from "../CustomButton/CustomButton";

const AddInstructorModal = ({ show, onHide, onSuccess }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const auth = useSelector((state) => state.auth);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      profilePicture: null, // Changed to null to represent no file selected
    },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email format").required("Required"),
      password: Yup.string()
        .matches(
          /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).*$/,
          "Password must include at least one uppercase letter, one number, and one special character"
        )
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Required"),
      profilePicture: Yup.mixed()
        .required("Profile picture is required")
        .test("fileSize", "File size must be less than 5MB", (value) => {
          return value ? value.size <= 5 * 1024 * 1024 : false;
        })
        .test(
          "fileType",
          "Only JPEG, PNG, or GIF images are allowed",
          (value) => {
            return value
              ? ["image/jpeg", "image/png", "image/gif"].includes(value.type)
              : false;
          }
        ),
    }),
    onSubmit: async (values) => {
      setError(null);
      setSuccess(null);
      try {
        const token = auth?.token;
        if (!token) {
          throw new Error("No authentication token found. Please log in.");
        }

        const formData = new FormData();
        formData.append("email", values.email);
        formData.append("password", values.password);
        formData.append("profile_picture", values.profilePicture);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}admin/instructors`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response?.data.success) {
          toast.success(response?.data?.message, {
            position: "top-center",
          });
          setSuccess(response.data.message); // e.g., "Instructor added successfully"
          formik.resetForm();
          if (onSuccess) onSuccess();
          onHide(); // Close modal after 2 seconds
        }
      } catch (error) {
        setError(
          error.response?.data?.message ||
            error.message ||
            "Failed to add instructor. Please try again."
        );
      }
    },
  });

  // Custom handler for file input
  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    formik.setFieldValue("profilePicture", file || null); // Update Formik state with file or null
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      aria-labelledby="addInstructorModalLabel"
    >
      <Modal.Header
        closeButton
        className="py-5 py-sm-10 py-md-15 py-lg-20 px-10 px-sm-20 px-md-30 px-lg-35"
      >
        <Modal.Title
          id="addInstructorModalLabel"
          className="ah-heading-06 fw-semibold"
        >
          Add New Instructor
        </Modal.Title>
      </Modal.Header>
      <div className="d-none">{error}</div>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body className="pt-8 pt-sm-15 pt-md-25 pt-lg-32 pb-32 px-10 px-sm-20 px-md-30 px-lg-35">
          {/* Email */}
          <Form.Group className="mb-16" controlId="email">
            <Form.Label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
              Email
            </Form.Label>
            <Form.Control
              type="text"
              name="email"
              placeholder="Enter your email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.email && !!formik.errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Password */}
          <div className="mb-16">
            <div className="position-relative">
              <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-control pe-40"
                placeholder="Enter your password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {showPassword ? (
                <svg
                  style={{
                    position: "absolute",
                    top: "67%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    width: "20px",
                  }}
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="eye-slash"
                  className="cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 512"
                >
                  <path
                    fill="currentColor"
                    d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"
                  ></path>
                </svg>
              ) : (
                <svg
                  style={{
                    position: "absolute",
                    top: "67%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    width: "20px",
                  }}
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="eye"
                  className="cursor-pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 576 512"
                >
                  <path
                    fill="currentColor"
                    d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"
                  ></path>
                </svg>
              )}
            </div>
            {formik.touched.password && formik.errors.password && (
              <div className="text-danger mt-5">{formik.errors.password}</div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-32">
            <div className="position-relative">
              <label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
                Confirm Password
              </label>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                className="form-control pe-40"
                placeholder="Confirm your password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {showConfirmPassword ? (
                <svg
                  style={{
                    position: "absolute",
                    top: "67%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    width: "20px",
                  }}
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="eye-slash"
                  className="cursor-pointer"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 512"
                >
                  <path
                    fill="currentColor"
                    d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z"
                  ></path>
                </svg>
              ) : (
                <svg
                  style={{
                    position: "absolute",
                    top: "67%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    width: "20px",
                  }}
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fas"
                  data-icon="eye"
                  className="cursor-pointer"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 576 512"
                >
                  <path
                    fill="currentColor"
                    d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z"
                  ></path>
                </svg>
              )}
            </div>
            {formik.touched.confirmPassword &&
              formik.errors.confirmPassword && (
                <div className="text-danger mt-5">
                  {formik.errors.confirmPassword}
                </div>
              )}
          </div>

          {/* Profile Picture */}
          <Form.Group className="mb-32" controlId="profilePicture">
            <Form.Label className="form-label black-100 fw-semibold mb-8 mb-lg-12">
              Upload Profile Picture
            </Form.Label>

            {/* Drag & Drop Area */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) formik.setFieldValue("profilePicture", file);
              }}
              className="border border-300 rounded-3 p-20 text-center position-relative"
              style={{ cursor: "pointer" }}
              onClick={() => document.getElementById("fileInput").click()}
            >
              {formik.values.profilePicture ? (
                <div className="position-relative d-inline-block">
                  {/* Image Preview */}
                  <img
                    src={URL.createObjectURL(formik.values.profilePicture)}
                    alt="Preview"
                    className="rounded-3"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                    }}
                  />
                  {/* Remove Button */}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      formik.setFieldValue("profilePicture", null);
                    }}
                    className="position-absolute translate-middle p-0 text-danger border border-black border-opacity-25 d-flex justify-content-center align-items-center fw-bold"
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      lineHeight: "1",
                      top: 0,
                      right: "-24px",
                    }}
                  >
                    ✕
                  </span>
                </div>
              ) : (
                <div className="text-muted">
                  Drag & drop your image here or browse
                </div>
              )}
            </div>

            {/* Hidden File Input */}
            <input
              id="fileInput"
              type="file"
              name="profilePicture"
              accept="image/*"
              className="d-none"
              onChange={(e) => {
                const file = e.currentTarget.files[0];
                formik.setFieldValue("profilePicture", file || null);
              }}
              onBlur={formik.handleBlur}
            />

            {formik.touched.profilePicture && formik.errors.profilePicture && (
              <div className="text-danger mt-5">
                {formik.errors.profilePicture}
              </div>
            )}
          </Form.Group>

          <CustomButton
            classes="btn btn-primary w-100"
            type="submit"
            label="Add Instructor"
            disabled={formik.isSubmitting}
            isSubmitting={formik.isSubmitting}
          />
        </Modal.Body>
      </Form>
    </Modal>
  );
};

export default AddInstructorModal;
